import { Injectable, computed, signal } from '@angular/core';

import { decodeJwtPayload, readStringClaim } from '@core/auth/jwt';

export interface SessionUser {
  readonly id: string | null;
  readonly name: string | null;
  /** The 10-digit number typed at login. Not in the token, so it is lost on reload. */
  readonly contactNumber: string | null;
  readonly roles: readonly string[];
}

/** Why the last session ended; the login page explains it once. */
export type SessionEndReason = 'expired' | 'blocked';

interface Session {
  readonly accessToken: string;
  readonly user: SessionUser;
}

const NAME_CLAIMS = [
  'name',
  'given_name',
  'unique_name',
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
];
const ID_CLAIMS = ['sub', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];

/**
 * The signed-in user and their access token, held in memory only. The refresh token
 * never reaches JavaScript: the server keeps it in an HttpOnly cookie.
 */
@Injectable({ providedIn: 'root' })
export class AuthSession {
  private readonly session = signal<Session | null>(null);
  private readonly lastEndReason = signal<SessionEndReason | null>(null);

  readonly accessToken = computed(() => this.session()?.accessToken ?? null);
  readonly user = computed(() => this.session()?.user ?? null);
  readonly isAuthenticated = computed(() => this.session() !== null);
  readonly endReason = this.lastEndReason.asReadonly();

  start(accessToken: string, roles: readonly string[], contactNumber?: string): void {
    const claims = decodeJwtPayload(accessToken);
    this.session.set({
      accessToken,
      user: {
        id: readStringClaim(claims, ID_CLAIMS),
        name: readStringClaim(claims, NAME_CLAIMS),
        // A refresh doesn't return the number, so keep the one from login.
        contactNumber: contactNumber ?? this.user()?.contactNumber ?? null,
        roles,
      },
    });
    this.lastEndReason.set(null);
  }

  clear(reason: SessionEndReason | null = null): void {
    this.session.set(null);
    this.lastEndReason.set(reason);
  }
}
